import { ActionReducerMapBuilder, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Plan } from 'models/Plan';

import { Subscription } from 'models/Subscription';

import BillingService from 'services/BillingService';

import { SubscriptionStatus } from 'models/SubscriptionStatus';

export interface BillingState {
    activeSubscriptions: Subscription[] | undefined;
    subscriptionStatuses: { [key: number]: SubscriptionStatus } | undefined;
    plans: Plan[] | null;
    fetching: boolean;
}

const initialState: BillingState = {
    activeSubscriptions: undefined,
    subscriptionStatuses: undefined,
    plans: null,
    fetching: true,
};

export interface BillingThunkResponse {
    plans: Plan[];
    activeSubscriptions: Subscription[];
    subscriptionStatuses: { [key: number]: SubscriptionStatus };
}

export const billingThunk = createAsyncThunk<BillingThunkResponse>('billing/get', async (_, thunkAPI) => {
    try {
        const [resPlans, resActiveSubs, resSubStatuses] = await Promise.all([
            BillingService.getPlans('SHOPIFY'),
            BillingService.getActiveSubscriptions(),
            BillingService.getSubscriptionStatuses(),
        ]);

        const plans = resPlans.plans;
        const activeSubscriptions = resActiveSubs;
        const subscriptionStatuses = resSubStatuses.subscription_statuses;
        return { plans, activeSubscriptions, subscriptionStatuses };
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const subscribeFreeThunk = createAsyncThunk<string, { ecomId: number; planId: number }, { rejectValue: string }>(
    'billing/subscribe/free',
    async ({ ecomId, planId }, { rejectWithValue, dispatch }) => {
        try {
            await BillingService.subscribeFree(ecomId, planId);
            dispatch(billingThunk());
            return '';
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const subscribeThunk = createAsyncThunk<string, { ecomId: number; planId: number }, { rejectValue: object }>(
    'billing/subscribe',
    async ({ ecomId, planId }, { rejectWithValue }) => {
        try {
            const redirectUrl = await BillingService.subscribe(ecomId, planId);
            return redirectUrl;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const cancelSubscriptionThunk = createAsyncThunk<boolean, number, { rejectValue: string }>(
    'billing/cancel',
    async (ecomId, { rejectWithValue, dispatch }) => {
        try {
            const resp = await BillingService.cancelSubscription(ecomId);

            dispatch(billingThunk());

            return true;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const cancelSubscriptionFreeThunk = createAsyncThunk<boolean, number, { rejectValue: string }>(
    'billing/cancel/free',
    async (ecomId, { rejectWithValue, dispatch }) => {
        try {
            const resp = await BillingService.cancelSubscriptionFree(ecomId);

            dispatch(billingThunk());

            return true;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const cancelPendingSubscriptionThunk = createAsyncThunk<boolean, number, { rejectValue: string }>(
    'billing/cancel-pending-subscription',
    async (ecomId, { rejectWithValue, dispatch }) => {
        try {
            const resp = await BillingService.cancelPendingSubscription(ecomId);

            dispatch(billingThunk());

            return true;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

const billingCases = (asyncThunk, builder: ActionReducerMapBuilder<BillingState>): void => {
    builder.addCase(asyncThunk.fulfilled, (state, action) => {
        state.plans = action.payload.plans;
        state.activeSubscriptions = action.payload.activeSubscriptions;
        state.subscriptionStatuses = action.payload.subscriptionStatuses;
        state.fetching = false;
    });
    builder.addCase(asyncThunk.pending, (state) => {
        state.fetching = true;
    });
    builder.addCase(asyncThunk.rejected, (state) => {
        state.fetching = false;
    });
};

const subscriptionFreeCases = (asyncThunk, builder: ActionReducerMapBuilder<BillingState>): void => {
    builder.addCase(asyncThunk.fulfilled, (state, action) => {
        state.fetching = false;
    });
    builder.addCase(asyncThunk.pending, (state) => {
        state.fetching = true;
    });
    builder.addCase(asyncThunk.rejected, (state) => {
        state.fetching = false;
    });
};

export const billingSlice = createSlice({
    name: 'billing',
    initialState: initialState,
    reducers: {
        setActiveSubscriptions: (state, action) => {
            state.activeSubscriptions = action.payload;
        },
        setSubscriptionStatuses: (state, action) => {
            state.subscriptionStatuses = action.payload;
        },
        setPlans: (state, action) => {
            state.plans = action.payload;
        },
    },
    extraReducers: (builder) => {
        subscriptionFreeCases(subscribeFreeThunk, builder);
        subscriptionFreeCases(cancelSubscriptionFreeThunk, builder);
        billingCases(billingThunk, builder);
        builder.addCase(subscribeThunk.pending, (state) => {
            state.fetching = true;
        });
        builder.addCase(subscribeThunk.fulfilled, (state) => {
            state.fetching = false;
        });
        builder.addCase(subscribeThunk.rejected, (state) => {
            state.fetching = false;
        });
        builder.addCase(cancelSubscriptionThunk.pending, (state) => {
            state.fetching = true;
        });
        builder.addCase(cancelSubscriptionThunk.fulfilled, (state) => {
            state.fetching = false;
        });
        builder.addCase(cancelSubscriptionThunk.rejected, (state) => {
            state.fetching = false;
        });
        builder.addCase(cancelPendingSubscriptionThunk.pending, (state) => {
            state.fetching = true;
        });
        builder.addCase(cancelPendingSubscriptionThunk.fulfilled, (state) => {
            state.fetching = false;
        });
        builder.addCase(cancelPendingSubscriptionThunk.rejected, (state) => {
            state.fetching = false;
        });
    },
});

export const { setActiveSubscriptions, setPlans, setSubscriptionStatuses } = billingSlice.actions;

export default billingSlice.reducer;
